import { useMemo, type ReactNode } from 'react';

import { TourProvider, WelcomePrompt, type TourAccess } from '@nucleus-commerce/admin-kit/tour';

import { canManageOrganizations } from '@/shared/session/roles';
import { session } from '@/shared/session/session';

import { platformTourProgress } from '../api/tours-api';
import { MANAGE, platformTourRegistry } from '../definitions';

export interface PlatformTourProviderProps {
  /** Runtime config `tours.enabled`. */
  enabled: boolean;
  /** Runtime config plus mock-mode opt-in (`welcomePromptAllowed`). */
  welcomeAllowed: boolean;
  children: ReactNode;
}

/**
 * Binds the shared tour engine to the console. Platform users have a role, not permission keys,
 * so steps that change things ask for `platform.manage`, which owners and admins hold.
 */
export function PlatformTourProvider({
  enabled,
  welcomeAllowed,
  children,
}: PlatformTourProviderProps) {
  const { me } = session.useSession();
  const role = me?.user.role;
  const access = useMemo<TourAccess>(() => {
    const granted = new Set(canManageOrganizations(role) ? [MANAGE] : []);
    return {
      can: (permission, mode = 'all') => {
        const list = Array.isArray(permission) ? permission : [permission];
        return mode === 'all'
          ? list.every((key) => granted.has(key))
          : list.some((key) => granted.has(key));
      },
    };
  }, [role]);
  const firstName = me?.user.name.split(' ')[0] ?? '';
  const values = useMemo(() => ({ firstName }), [firstName]);
  const active = enabled && me != null && !me.twoFactorRequired;

  return (
    <TourProvider
      registry={platformTourRegistry}
      access={access}
      progressAdapter={platformTourProgress}
      app="platform"
      enabled={active}
      values={values}
      welcomeTourId="platform.welcome"
    >
      {children}
      <WelcomePrompt allowed={active && welcomeAllowed} firstName={firstName} />
    </TourProvider>
  );
}
